import { useLayoutEffect, useRef, useState } from 'react';
import { formatNumber } from '../lib/utils';
import {
	COUNTER_LOCALE_ATTR,
	COUNTER_MODE_ATTR,
	COUNTER_MODE_ENTRANCE,
	COUNTER_MODE_INSTANT,
	COUNTER_VALUE_ATTR,
} from '../lib/counter-contract';
import type { Locale } from '@worldcoo/monolith/core';

/**
 * The rAF loop writes `textContent` and React re-renders once per animation: six counters at 60fps were
 * ~36 commits in the worst window of page load. The entrance effect renders zero on both sides; `instant`
 * renders the real value and suppresses the hydration warning (TEC-3689, TEC-3695).
 * What each mode guarantees: docs/notes/ui/animate-number.md
 */

/** Exponential decay rate of the count-up, in units per second: 8 settles a counter in ~0.6s. */
const DECAY_PER_SECOND = 8;

export function AnimateNumber({
	locale,
	value,
	suffix,
	testId,
	onAnimateComplete,
	decimalDigits = 0,
	className,
	instant = false,
	alwaysGroup = false,
}: {
	locale: Locale;
	value: number;
	suffix?: string;
	testId?: string;
	onAnimateComplete?: () => void;
	decimalDigits?: number;
	className?: string;
	instant?: boolean;
	/** es-ES formats 1303 as "1303", which reads as a different magnitude beside a grouped figure. */
	alwaysGroup?: boolean;
}) {
	const ref = useRef<HTMLSpanElement>(null);
	const rafRef = useRef(0);
	const isFirstRef = useRef(true);
	const onCompleteRef = useRef(onAnimateComplete);
	onCompleteRef.current = onAnimateComplete;

	const target = value ?? 0;

	const fmt = (n: number) => `${formatNumber(locale, n, decimalDigits, { alwaysGroup })}${suffix ? ` ${suffix}` : ''}`;
	const fmtRef = useRef(fmt);
	fmtRef.current = fmt;

	// Both first paints are values the server reproduces exactly.
	const [settledText, setSettledText] = useState(() => fmt(instant ? target : 0));
	const currentRef = useRef(instant ? target : 0);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return undefined;

		const targetText = fmtRef.current(target);
		const isFirst = isFirstRef.current;
		isFirstRef.current = false;

		if (isFirst) {
			currentRef.current = instant ? target : 0;

			if (instant) {
				// `suppressHydrationWarning` skipped the text patch, so the DOM may hold the SERVER's figure.
				if (el.textContent !== targetText) el.textContent = targetText;
				return undefined;
			}

			if (target === 0) return undefined;
		}

		// rAF never fires in a hidden tab; completion still reports, which `portal-animate-number` waits on.
		const prefersReducedMotion =
			typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		const isDocumentHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

		if (instant || prefersReducedMotion || isDocumentHidden) {
			currentRef.current = target;
			if (el.textContent !== targetText) el.textContent = targetText;
			setSettledText(targetText);
			onCompleteRef.current?.();
			return undefined;
		}

		// Before the first rAF, or it flashes.
		el.textContent = fmtRef.current(currentRef.current);

		let prev = 0;

		const tick = (now: number) => {
			// Not `performance.now()`: it can diverge from rAF and make dt negative on the first tick.
			const dt = prev ? (now - prev) / 1000 : 0;
			prev = now;

			currentRef.current += (target - currentRef.current) * (1 - Math.exp(-DECAY_PER_SECOND * dt));

			const text = fmtRef.current(currentRef.current);

			// The float keeps moving after the visible text is final.
			if (text === targetText) {
				currentRef.current = target;
				el.textContent = targetText;
				setSettledText(targetText);
				onCompleteRef.current?.();
				return;
			}

			if (el.textContent !== text) {
				el.textContent = text;
			}

			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);

		return () => cancelAnimationFrame(rafRef.current);
		// Formatting deps, or a locale change keeps the stale `settledText`; a re-run settles at once.
	}, [target, instant, locale, decimalDigits, suffix]);

	return (
		// Never a `<div>`: callers nest it in a `<p>`, which the parser closes early, failing hydration
		// structurally (#418 `args[]=HTML`, TEC-3690).
		<span
			ref={ref}
			className={`inline-block tracking-tight tabular-nums${className ? ` ${className}` : ''}`}
			data-testid={testId}
			// Read by the client-error counter snapshot; without the mode an unhydrated zero reads as a mismatch.
			{...{
				[COUNTER_VALUE_ATTR]: value,
				[COUNTER_MODE_ATTR]: instant ? COUNTER_MODE_INSTANT : COUNTER_MODE_ENTRANCE,
				[COUNTER_LOCALE_ATTR]: locale,
			}}
			// Translators restate totals with wrong separators; the labels stay translatable (see the note).
			translate="no"
			// Only `instant` can legitimately differ from the server.
			suppressHydrationWarning={instant}
		>
			{settledText}
		</span>
	);
}
